import React from 'react';
import { ArrowRight, BarChart3, Handshake, Home } from 'lucide-react';
import { getEnabledObjectives, publicProspectPageConfig } from '../../config/publicProspectPageConfig';
import { isIntentReceived } from './publicOwnerResponseState';

const ICONS = {
  profitability_guide: BarChart3,
  property_value: Home,
  property_sale: Handshake,
};

export default function ObjectiveSelector({
  language,
  copy,
  confirmation,
  receivedIntents,
  onSelect,
  disabled,
}) {
  const objectives = getEnabledObjectives(publicProspectPageConfig);
  return (
    <section className="space-y-4" aria-labelledby="objectives-heading">
      <div>
        <h2
          id="objectives-heading"
          className="prospect-display text-[1.9rem] font-semibold leading-[1.08] tracking-tight text-[#0B1F2A] sm:text-[2rem]"
        >
          {copy.objectivesHeading}
        </h2>
        <p className="prospect-ui mt-1 text-[1.0625rem] leading-relaxed text-[#1f4a52]">{copy.objectivesSubtext}</p>
      </div>
      {receivedIntents?.includes('legacy_owner_response') ? (
        <p className="prospect-ui rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          {confirmation.legacyNote}
        </p>
      ) : null}
      <div className={`grid gap-3 ${objectives.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {objectives.map((objective) => {
          const done = isIntentReceived(receivedIntents, objective.intent);
          const Icon = ICONS[objective.intent] || Home;
          const label = language === 'en' ? objective.en.label : objective.fr.label;
          const description = language === 'en' ? objective.en.description : objective.fr.description;
          const sale = objective.visual === 'sale';
          return (
            <button
              key={objective.intent}
              type="button"
              disabled={disabled || done}
              onClick={() => onSelect(objective.intent)}
              className={`flex min-h-[3.25rem] items-start gap-3 rounded-2xl p-4 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91] focus-visible:ring-offset-2 disabled:cursor-not-allowed ${
                sale
                  ? 'border-2 border-[#c4a574] bg-[#004747] text-white shadow-md'
                  : 'border border-gray-200 bg-[#edf3ef] text-[#004747]'
              } ${done ? 'opacity-70' : 'hover:brightness-[0.98]'}`}
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="flex-1">
                <span className="prospect-display block text-[1.375rem] font-semibold leading-[1.1]">
                  {label}
                </span>
                <span
                  className={`prospect-ui mt-1 block text-sm leading-relaxed ${
                    sale ? 'text-white/85' : 'text-[#1f4a52]'
                  }`}
                >
                  {done ? confirmation.completed : description}
                </span>
              </span>
              <ArrowRight className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" />
            </button>
          );
        })}
      </div>
      {publicProspectPageConfig.guide.enabled ? (
        <GuideCard language={language} onSelect={() => onSelect('profitability_guide')} disabled={disabled} />
      ) : null}
    </section>
  );
}

function GuideCard({ language, onSelect, disabled }) {
  const guide = publicProspectPageConfig.guide;
  const forms = publicProspectPageConfig.forms.profitability_guide[language] || publicProspectPageConfig.forms.profitability_guide.fr;
  return (
    <aside className="rounded-2xl border border-[#c4a574]/40 bg-[#edf3ef] p-4">
      <h3 className="prospect-display text-[1.375rem] font-semibold leading-[1.1] text-[#0B1F2A]">
        {language === 'en' ? guide.title.en : guide.title.fr}
      </h3>
      <p className="prospect-ui mt-1 text-sm leading-relaxed text-[#1f4a52]">
        {language === 'en' ? guide.subtitle.en : guide.subtitle.fr}
      </p>
      <button
        type="button"
        disabled={disabled}
        onClick={onSelect}
        className="prospect-ui mt-3 inline-flex items-center gap-2 rounded-xl bg-[#004747] px-4 py-2 text-[1.0625rem] font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007c91]"
      >
        {forms.cta}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </aside>
  );
}
